import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/firestore";
import { getStorageJSON } from "../../utils/helpers";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FoodCard from "./components/FoodCard";

function parseCouponNumber(value, fallback = 0) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const normalized = String(value ?? "")
    .replace(/,/g, "")
    .replace(/[^\d.-]/g, "")
    .trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function isFreeDeliveryCoupon(coupon) {
  const type = String(coupon?.discountType || coupon?.type || "").toLowerCase();
  const code = String(coupon?.code || "").toUpperCase();
  return Boolean(
    coupon?.freeDelivery ||
    coupon?.isFreeDelivery ||
    coupon?.deliveryFree ||
    type === "free_delivery" ||
    type === "free-shipping" ||
    type === "freeshipping" ||
    code.includes("FREEDEL") ||
    code.includes("FREEDELIVERY")
  );
}

function inferPercentFromCode(code) {
  const match = String(code || "").match(/(\d{1,2})/);
  const value = Number(match?.[1] || 0);
  return Number.isFinite(value) && value > 0 && value <= 80 ? value : 0;
}

function getCouponMeta(coupon) {
  const offerType = String(coupon?.offerType || "").toLowerCase();
  const target = String(coupon?.appliesToCategory || "all").toLowerCase();
  const discountPct = parseCouponNumber(
    coupon?.discount ?? coupon?.discountPercent ?? coupon?.percentOff ?? coupon?.percentage,
    0
  );
  const flatDiscount = parseCouponNumber(
    coupon?.flatDiscount ?? coupon?.amountOff ?? coupon?.value ?? coupon?.discountAmount,
    0
  );
  const inferredPct = discountPct > 0 ? discountPct : inferPercentFromCode(coupon?.code);
  const freeDelivery = isFreeDeliveryCoupon(coupon);

  if (freeDelivery) {
    return {
      sortWeight: 1000,
      badge: "FREE DELIVERY",
      tone: "free",
      target,
      detail: "Free delivery on eligible orders",
    };
  }

  if (inferredPct > 0) {
    const comboFlag = target === "combo" || offerType === "combo_percentage";
    return {
      sortWeight: inferredPct,
      badge: `${Math.round(inferredPct)}% OFF`,
      tone: comboFlag ? "combo" : "percent",
      target,
      detail: comboFlag ? "Only on combo items" : "Instant percentage savings",
    };
  }

  if (flatDiscount > 0) {
    return {
      sortWeight: flatDiscount,
      badge: `Rs ${Math.round(flatDiscount)} OFF`,
      tone: "flat",
      target,
      detail: "Flat cash discount",
    };
  }

  return {
    sortWeight: 0,
    badge: "Special Offer",
    tone: "special",
    target,
    detail: "Limited deal",
  };
}

function getTargetLabel(target) {
  if (target === "combo") return "Combo only";
  if (target === "snacks") return "Snacks only";
  if (target === "sweets") return "Sweets only";
  return "All items";
}

function getUrgencyLabel(expiry) {
  if (!expiry) return "No expiry";
  const end = new Date(expiry);
  const daysLeft = Math.ceil((end.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  if (!Number.isFinite(daysLeft)) return "Limited time";
  if (daysLeft <= 0) return "Ends today";
  if (daysLeft <= 3) return `${daysLeft} day(s) left`;
  return `Valid till ${expiry}`;
}

export default function Offers() {
  const [items, setItems] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [copiedCode, setCopiedCode] = useState("");
  const [loadingItems, setLoadingItems] = useState(true);
  const [loadingCoupons, setLoadingCoupons] = useState(true);
  const [offerError, setOfferError] = useState("");

  useEffect(() => {
    let active = true;
    const couponCacheKey = "offers_cache_v1";
    const itemCacheKey = "offer_items_cache_v1";

    const fetchItems = async () => {
      try {
        const itemSnap = await getDocs(collection(db, "items"));
        const nextItems = itemSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (active) {
          setItems(nextItems);
          localStorage.setItem(itemCacheKey, JSON.stringify(nextItems));
          setLoadingItems(false);
        }
      } catch {
        if (active) {
          const cached = getStorageJSON(itemCacheKey, []);
          setItems(Array.isArray(cached) ? cached : []);
          setLoadingItems(false);
          setOfferError("Live item sync failed. Showing cached offer items.");
        }
      }
    };

    fetchItems();

    const intervalId = setInterval(fetchItems, 60000);
    const offersUnsub = onSnapshot(
      collection(db, "offers"),
      (snap) => {
        const now = new Date();
        const validCoupons = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((coupon) => coupon.active !== false)
          .filter((coupon) => !coupon.expiry || new Date(coupon.expiry) >= now)
          .sort((a, b) => getCouponMeta(b).sortWeight - getCouponMeta(a).sortWeight);

        setCoupons(validCoupons);
        localStorage.setItem(couponCacheKey, JSON.stringify(validCoupons));
        setLoadingCoupons(false);
        setOfferError("");
      },
      () => {
        const cachedCoupons = getStorageJSON(couponCacheKey, []);
        setCoupons(Array.isArray(cachedCoupons) ? cachedCoupons : []);
        setLoadingCoupons(false);
        setOfferError("Live coupon sync failed. Showing cached coupons.");
      }
    );

    const visibilityHandler = () => {
      if (!document.hidden) {
        fetchItems();
      }
    };

    document.addEventListener("visibilitychange", visibilityHandler);

    return () => {
      active = false;
      clearInterval(intervalId);
      offersUnsub();
      document.removeEventListener("visibilitychange", visibilityHandler);
    };
  }, []);

  const offerItems = useMemo(() => {
    return items.filter((item) => Number(item.prices?.[250]) > 0).slice(0, 12);
  }, [items]);

  const copyCoupon = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(""), 1500);
    } catch {
      setCopiedCode("Copy failed");
    }
  };

  return (
    <div className="home">
      <Navbar />

      <div className="customer-page">
        <div className="customer-page-header">
          <h2>Special Offers</h2>
          <p>Live deals and coupons auto-refreshed from offers collection.</p>
        </div>

        <section className="customer-card settings-section">
          <h3>Coupon Codes</h3>
          {offerError ? <p className="form-status">{offerError}</p> : null}
          {loadingCoupons ? (
            <p>Loading coupons...</p>
          ) : !coupons.length ? (
            <p>No active coupons right now. Check again shortly.</p>
          ) : (
            <div className="coupon-grid">
              {coupons.map((coupon) => {
                const meta = getCouponMeta(coupon);
                return (
                  <article key={coupon.id} className={`coupon-card coupon-card--${meta.tone}`}>
                    <div className="row-between">
                      <h4>{coupon.code}</h4>
                      <span className="status-chip">{meta.badge}</span>
                    </div>

                    <div className="coupon-highlight-row">
                      <span className="coupon-target-chip">{getTargetLabel(meta.target)}</span>
                      <span className="coupon-urgency-chip">{getUrgencyLabel(coupon.expiry)}</span>
                    </div>

                    <p className="coupon-detail-line">{meta.detail}</p>

                    <div className="coupon-meta-list">
                      <p>Min Order: {coupon.minOrder ? `Rs ${coupon.minOrder}` : "No minimum"}</p>
                      <p>Max Discount: {coupon.maxDiscount ? `Rs ${coupon.maxDiscount}` : "No cap"}</p>
                      <p>Valid till: {coupon.expiry || "No expiry"}</p>
                    </div>

                    <div className="coupon-terms">
                      <b>Terms & Conditions</b>
                      <ul>
                        <li>Applicable on one account up to {coupon.perUserLimit || 1} time(s).</li>
                        <li>Cannot be combined with other promos.</li>
                        <li>Only valid for active serviceable areas.</li>
                      </ul>
                    </div>

                    <button className="primary-action" onClick={() => copyCoupon(coupon.code)}>
                      {copiedCode === coupon.code ? "Copied" : "Copy Coupon"}
                    </button>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <div className="items-grid">
          {loadingItems ? (
            <p>Loading offer items...</p>
          ) : (
            offerItems.map((item) => (
              <FoodCard key={item.id} item={item} />
            ))
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
